<script>
import Vue from 'vue'
import moment from 'moment'
import AgGridMethods from '@/Mixins/SigmaClientNew/AgGridMethods'
import AgGridPaymentTransactionsColumns from '@/Mixins/SigmaClientNew/AgGridPaymentTransactionsColumns'
import { AgGridVue } from 'ag-grid-vue'
import AgGridBtnRender from '@/components/SigmaClientNew/AgGridBtnRender'
import AgGridSearchFilter from '@/components/SigmaClientNew/AgGridSearchFilter'
import AgGridSearchFloatingFilter from '@/components/SigmaClientNew/AgGridSearchFloatingFilter'
import AgGridTextFloatingFilter from '@/components/SigmaClientNew/AgGridTextFloatingFilter'
import AgGridSingleSelectFilter from '@/components/SigmaClientNew/AgGridSingleSelectFilter'
import AgGridDateRangeFilter from '@/components/SigmaClientNew/AgGridDateRangeFilter'
import AgGridDateRangeFloatingFilter from '@/components/SigmaClientNew/AgGridDateRangeFloatingFilter'
import { mapGetters } from 'vuex'
import { getServerFilterConfig, GRID_FILTER_PARAMS, SEEDED_FILTER_COLUMNS, statusFilterValues, pickedValues, saveFilterModel, readFilterModel } from '@/Mixins/SigmaClientNew/transactionGridFilters'

export default Vue.extend({
  name: 'TransactionsConfPaymentList',

  components: {
    AgGridVue
  },
  mixins: [ AgGridMethods, AgGridPaymentTransactionsColumns ],

  props: {
    // Name of input field.  Not really necessary, but helpful if we use the form object down the road
    name: {
      required: true,
      type: String
    },

    iteration: {
      required: false,
      type: Number
    },

    classes: {
      required: false,
      type: String,
      default: 'ag-theme-balham'
    },

    rows: {
      required: true
    },

    // Ag-grid column defs
    columns: {
      required: false,
      type: Array,
      default: undefined
    },

    // Ag-grid Options
    options: {
      required: false,
      type: Object,
      default: undefined
    },
    aggridColumns: {
      required: false,
      type: Array
    },
    baseQuery: {
      required: false,
      type: Object,
      default: () => ({})
    },
    /**
     * Whether the grid fetches its rows as soon as it is built. A view showing more than one list
     * can hold one back and ask for its rows itself - through loadInitialRows, once the list it
     * reads first has its own - rather than have them all query at once.
     */
    autoLoad: {
      required: false,
      type: Boolean,
      default: true
    },
    emitSelect: {
      required: false,
      type: Boolean,
      default: false
    },
    loadingRelated: {
      required: false,
      type: Boolean,
      default: false
    },
    voidingPayment: {
      required: false,
      type: Boolean,
      default: false
    },
    postingPayment: {
      required: false,
      type: Boolean,
      default: false
    },
    postingCheck: {
      required: false,
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      isError: false, // If the input or selected field is incorrect, show an error
      isLoading: false,
      scope: this,
      gridApi: null,
      columnApi: null,
      defaultColDefs: undefined,
      defaultGridOptions: undefined,
      changes: [],
      frameworkComponents: null,
      rowHeight: 40,
      domLayout: 'autoHeight',
      filterDebounceTimer: null,
      lastFilterModel: null,
      /**
       * Writes to the filter model the grid is making itself - the view's own criteria seeded into
       * the columns, the criteria put back after a refresh - which are what the rows already answer
       * to. Each one is a filter change to ag-grid, and this is how many of them to sit out before
       * a change is the user's and worth fetching for.
       */
      quietFilterWrites: 0,
      filtersStartedOver: true,
      defaultFiltersSeeded: false,
      initialFetch: false,
      // What the rows the grid opens on are asked for with, held until the view is ready for them
      initialLoad: null,
      // The view asked for those rows before the grid was built, so they are fetched once it is
      initialLoadRequested: false,
      extendedFilters: {}
    }
  },

  computed: {
    ...mapGetters({
      currentApsPage: 'getCurrentApPage',
      currentPaymentsPage: 'getCurrentPaymentPage',
      currentCheckPage: 'getCurrentCheckPage',
      currentPettycashPage: 'getCurrentPcPage',
      currentJesPage: 'getCurrentJePage',
      currentPosPage: 'getCurrentPoPage',
      selection: 'getSelectedTransactions',
      getCurrencyByBatchId: 'getCurrencyByBatchId',
      getFilters: 'getFilters',
      isFilterLoading: 'getTransactionFilterStatus'
    }),
    currentPage () {
      const routeName = {
        'Payments': 'currentPaymentsPage',
        'PaymentProcessing': 'currentPaymentsPage',
        'CheckProcessing': 'currentCheckPage',
        'Checks': 'currentPage'
      }
      return this[routeName[this.$route.name]]
    },
    serverFilterConfig () {
      return getServerFilterConfig(this.$route.name)
    },
    columnDefs () {
      if (this.columns && Array.isArray(this.columns)) {
        return this.columns
      } else {
        return this.defaultColDefs
      }
    },
    gridOptions () {
      return this.options || this.defaultGridOptions
    },
     // Any groups with at least one transaction selected
    selectedNodes () {
      let aggregatedNodes = []
      this.gridApi.forEachNode((node) => {
        if (node.selected) {
          aggregatedNodes.push(node)
        }
      })
      return aggregatedNodes
    },

    // Since we are now sending invoices grouped by invoice, this helper makes each group look like a transaction
    simulatedPaymentTransactionData () {
      if (!this.selectedNodes.length) return []

      let transactions = []
      this.selectedNodes.forEach((node) => {
        const transaction = {
          attributes: node.data.attributes
        }
        transactions.push(transaction)
      })

      return transactions
    },
    width () {
      return `calc(100vw - 200px)`
    }
  },

  methods: {
    // Ag-grid
    onGridReady (params) {
      if (!params) return
      this.gridApi = params.api
      this.columnApi = params.columnApi
      if (this.isFilterLoading) this.gridApi.showLoadingOverlay()
      if (this.$el) {
        this.$el.addEventListener('click', this.handleGridClick)
      }
      const saved = this.savedFilterState()
      this.extendedFilters = saved.extendedFilters || {}
      const model = this.restoreSavedFilterModel(saved) ? saved.model : this.seedDefaultFilters()
      // The filters are settled either way; the rows are what waits on the view
      this.initialLoad = { page: saved.page, model, extendedFilters: saved.extendedFilters || {} }
      if (this.autoLoad || this.initialLoadRequested) this.loadInitialRows()
    },
    /**
     * Fetches the rows the grid opens on, for the views that hold a grid back rather than have
     * every list on them query at once. Asking before the grid is built is remembered and done as
     * soon as it is; asking twice loads once.
     */
    loadInitialRows () {
      if (!(this.gridApi && this.initialLoad)) {
        this.initialLoadRequested = true
        return
      }
      const { page, model, extendedFilters } = this.initialLoad
      this.initialLoad = null
      this.initialLoadRequested = false
      return this.loadRows(page, model, extendedFilters)
    },
    loadRows (page = 1, filterModel = null, extendedFilters = {}) {
      if (!this.gridApi || !this.serverFilterConfig) return
      this.extendedFilters = extendedFilters || {}
      if (this.filtersStartedOver) {
        this.filtersStartedOver = false
        return this.fetchFilteredRows(this.buildFilterQuery({}, page, extendedFilters, true), {})
      }
      const model = this.activeFilterModel(filterModel || this.gridApi.getFilterModel())
      return this.fetchFilteredRows(this.buildFilterQuery(model, page, extendedFilters), model)
    },
    activeFilterModel (filterModel) {
      const active = {}
      Object.keys(filterModel || {}).forEach(columnId => {
        const model = filterModel[columnId]
        if (!model) return
        if (Array.isArray(model.values) && !model.values.length) return
        active[columnId] = model
      })
      return active
    },
    savedFilterState () {
      const saved = readFilterModel(this.$route.name) || {}
      const model = this.activeFilterModel(saved.model)
      return { model: Object.keys(model).length ? model : null, page: saved.page || 1, extendedFilters: saved.extendedFilters || {} }
    },
    restoreSavedFilterModel (saved) {
      const filterModel = saved && saved.model
      if (!this.gridApi || !this.serverFilterConfig || !filterModel) return false
      // The user's own filters, so the defaults have nothing left to seed over
      this.defaultFiltersSeeded = true
      this.setFilterModelQuietly(filterModel)
      return true
    },
    seedDefaultFilters () {
      if (this.defaultFiltersSeeded || !this.gridApi || !this.columnApi) return null
      const filterModel = this.gridApi.getFilterModel() || {}
      const seeded = Object.assign({}, filterModel)
      let seededAny = false
      let valuesPending = false

      // The source codes are seeded before the statuses, since whether the DC and reversal ones are
      // among them decides whether the status filter opens on VOIDED
      SEEDED_FILTER_COLUMNS.forEach(columnId => {
        // Only where the view has the column, and only while the user has not filtered on it
        if (!this.columnApi.getColumn(columnId) || filterModel[columnId]) return
        // The banks and source codes are fetched in their own right, so they can still be on the way
        if (!this.filterValues(columnId).length) {
          valuesPending = true
          return
        }
        const ticked = this.defaultFilterSelection(columnId)
        if (!ticked) return
        seeded[columnId] = { filterType: 'set', values: ticked }
        seededAny = true
      })

      // Nothing waiting on data means this is as seeded as the view gets, and the user owns the
      // filters from here on
      if (!valuesPending) this.defaultFiltersSeeded = true
      if (!seededAny) return null
      this.defaultFiltersSeeded = true
      this.setFilterModelQuietly(seeded)
      return seeded
    },
    /**
     * The values a set filter starts ticked, or null when it starts on all of them and there is
     * nothing to seed. What the rows on screen already answer to, never a new set of criteria: the
     * status filter offers every status the transaction goes through and opens on the ones the view
     * is read in, so the others are there to be asked for rather than hidden away.
     * @param columnId
     * @return {Array|null}
     */
    defaultFilterSelection (columnId) {
      const values = this.filterValues(columnId)
      if (!values.length) return null    // The values are not loaded yet, so there is nothing to tick

      const scoped = columnId === 'status' ? (this.baseQuery || {}).status__in : null
      // A column the view says nothing about opens the way the filter does, on all of them
      if (!(Array.isArray(scoped) && scoped.length)) return null

      const ticked = values.filter(value => scoped.includes(value))
      // Everything ticked is what a filter does anyway, so it is left alone. Nothing ticked would be
      // a list of no rows at all, which is not what the view is asking for either.
      if (!ticked.length || ticked.length === values.length) return null
      return ticked
    },
    /**
     * Builds the API query for the current column filters
     * @param filterModel   ag-grid filter model
     * @param page          Page to request
     * @return {Object}
     */
    buildFilterQuery (filterModel = {}, page = 1, extendedFilters = {}, viewCriteriaOnly = false) {
      let query = Object.assign(this.baseFilterQuery(), { page })
      if (this.$route.name !== 'Payments') query.nest_lines = false
      if (this.$route.name !== 'Checks') delete query.nest_lines
      delete query.payment_type__in
      delete query.search

      Object.entries(filterModel || {}).forEach(([columnId, model]) => {
        if (!model) return
        const values = Array.isArray(model.values) ? pickedValues(model.values) : []
        const textValue = typeof model.filter === 'string' ? model.filter.trim() : ''

        switch (columnId) {
          case 'ledger': {
            // Ledger ids are matched as ids rather than searched for, several if a list is typed
            const ledgers = textValue.split(/[\s,]+/).filter(Boolean)
            if (ledgers.length) query.ledger__in = ledgers
            break
          }
          case 'number': {
            // An emptied box is asking for nothing, so the param is left off altogether
            const number = textValue.split(/[\s,]+/).filter(Boolean)
            if (number.length) query.number__icontains = number
            break
          }
          case 'vendor':
            // The vendor filter searches the API, so its model already holds vendor ids. The check
            // and payment runs read them by the param their own filter bar asks by.
            if (values.length) {
              if (['Checks', 'CheckProcessing', 'PaymentProcessing'].includes(this.$route.name)) {
                query.vendor = values
              } else {
                query.vendor__in = values
              }
            }
            break
          case 'description':
            if (textValue) query.description__icontains = textValue
            break
          case 'payment_type':
            if (values.length) query.payment_type__in = values
            break
          case 'status':
            // The statuses ticked are asked for instead of the ones the view opens on, which are
            // what the filter offers in the first place
            if (values.length) query.status__in = values
            break
          case 'amount_decimal':
            if (textValue) query.amount_decimal__icontains = textValue
            break
          case 'date':
            Object.assign(query, this.dateRangeParams('date', model))
            break
          case 'due_date':
            Object.assign(query, this.dateRangeParams('due_date', model))
            break
          case 'batch':
            // The batch filter searches the API, so its model already holds batch ids
            if (values.length) query.batch = values
            break
          case 'bank':
            if (values.length) {
              // The banks picked here are the whole of it, so the view's single bank steps aside
              if (['Checks', 'CheckProcessing', 'PaymentProcessing'].includes(this.$route.name)) {
                query.batch__bank = this.idsForValues('getAllBanks', 'name', values)
              } else {
                query.batch__bank__in = this.idsForValues('getAllBanks', 'name', values)
              }
            }
            break
          case 'is_notified':
            if (values.length) query.is_notified__in = values
            break
          default:
            // The remaining columns are text filters, all served by the keyword search
            if (textValue) query.search = textValue
        }
      })

      if (extendedFilters && extendedFilters.search) {
        query.search = extendedFilters.search
      }
      if (extendedFilters && extendedFilters.payment_type__in) {
        query.payment_type__in = extendedFilters.payment_type__in
      }
      return query
    },
    /**
     * The criteria for a date column, one param for each end of the span it is read over. Each end
     * stands on its own: a date to read from with no date to read to is everything since it, and
     * the other way round is everything up to it.
     * @param field   Field the dates are for, e.g. 'date'
     * @param model   The filter's model, whose dates may be either one or both
     * @return {Object}
     */
    dateRangeParams (field, model) {
      // The dates are handed over as 'YYYY-MM-DD', or with a time on them, of which only the day is
      // asked for - a timestamp would carry this browser's offset into it
      const asDay = (value) => (typeof value === 'string' && value ? moment(value.split(' ')[0], 'YYYY-MM-DD', true) : null)
      const params = {}
      const from = asDay(model.dateFrom)
      const to = asDay(model.dateTo)
      if (from && from.isValid()) params[`${field}__gte`] = from.format('YYYY-MM-DD')
      if (to && to.isValid()) params[`${field}__lte`] = to.format('YYYY-MM-DD')
      return params
    },
    idsForValues (getter, attribute, values) {
      return (this.$store.getters[getter] || [])
        .filter(record => record && values.includes(record[attribute]))
        .map(record => record.id)
    },
    filterModelKey (filterModel) {
      const model = filterModel || {}
      return JSON.stringify(Object.keys(model).sort().map(columnId => {
        const criteria = model[columnId] || {}
        return [columnId, Object.keys(criteria).sort().map(key => [key, criteria[key]])]
      }))
    },
    setFilterModelQuietly (filterModel) {
      this.quietFilterWrites += 1
      this.gridApi.setFilterModel(filterModel)
      if (!(filterModel && Object.keys(filterModel).length)) this.quietFilterWrites = 0
    },
    handleGridClick (event) {
      const target = event.target
      if (target.classList.contains('clickable-link')) {
        const path = target.dataset.path // Extract the path from the dataset
        this.saveCurrentFilterModel()
        this.$router.push(path) // Navigate programmatically using Vue Router
      }
    },
    saveCurrentFilterModel () {
      if (!this.gridApi || !this.serverFilterConfig) return
      const filterModel = this.activeFilterModel(this.gridApi.getFilterModel())
      const saved = readFilterModel(this.$route.name) || {}
      saveFilterModel(this.$route.name, filterModel, this.currentPage || 1, saved.extendedFilters || {})
    },
    onFirstDataRendered (params) {
      if (!params) return
      if (params.api.gridPanel.bodyHeight) params.api.sizeColumnsToFit()
    },
    filterValues (columnId) {
      if (columnId === 'status') return statusFilterValues(this.$route.name, this.baseQuery)
      if (columnId === 'bank') {
        return (this.$store.getters.getAllBanks || [])
          .map(bank => bank && bank.name)
          .filter(Boolean)
      }
      return []
    },
    onChangeVisibleColumns () {
      this.$nextTick(() => {
        this.gridApi.sizeColumnsToFit()
      })
    },
    baseFilterQuery () {
      const storeFilters = Object.assign({}, this.getFilters)
      GRID_FILTER_PARAMS.forEach(param => delete storeFilters[param])
      return Object.assign(storeFilters, this.baseQuery || {})
    },
    async syncStoreFilters (query) {
      const cleared = GRID_FILTER_PARAMS.filter(param => !query.hasOwnProperty(param))
      for (const param of cleared) {
        await this.$store.dispatch('unsetFilter', param)
      }
      // A copy, since the fetch actions add their own params to the query they are given
      await this.$store.dispatch('setFilter', Object.assign({}, query))
    },
    /**
     * Fetches the rows matching the query. The results go through the store, which is what feeds
     * the 'rows' prop and the record count the pagination is built from.
     * @param query
     * @param filterModel   The criteria the query was built from, put back if the refresh loses them
     */
    async fetchFilteredRows (query, filterModel = null) {
      const config = this.serverFilterConfig
      if (!config) return
      // What the rows about to be fetched reflect, so the same criteria are not filtered on twice
      this.lastFilterModel = this.filterModelKey(filterModel)
      this.$store.commit('SET_FILTER_STATUS', true)
      const isDraftChecks = (this.gridApi.context.contextParams.providedBeanInstances.gridOptions.context.tableName === 'draftChecks_transactions_conf_list') || false
      try {
        await this.syncStoreFilters(query)
        // Only the views that remember their filters between visits have somewhere to put them
        if (config.filterInfoAction) {
          await this.$store.dispatch(config.filterInfoAction, Object.assign({}, query))
        }
        await Promise.all([
          this.$store.dispatch(isDraftChecks ? 'resetCheckMeta' : config.resetMetaAction),
          this.$store.dispatch(isDraftChecks ? 'clearCheckState' : config.clearAction, true)
        ])
        this.gridApi.showLoadingOverlay()
        this.clearSelection()
        // Filtering starts from the first page again, which the view showing the rows has to know
        this.$emit('filtersApplied')
        const response = await this.$store.dispatch(isDraftChecks ? 'fetchPrintableChecksWithQuery' : config.fetchAction, query)
        if (response) {
          await Promise.all([response])
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.$store.commit('SET_FILTER_STATUS', false)
        this.restoreFilterModel(filterModel)
        // The API is done with this list, which is what a view waiting to load another one hears
        this.$emit('rowsLoaded')
      }
    },
    restoreFilterModel (filterModel) {
      if (!(filterModel && Object.keys(filterModel).length) || !this.gridApi) return
      const current = this.activeFilterModel(this.gridApi.getFilterModel())
      // A column keeps its criteria whichever filter triggered the refresh: what was typed into the
      // text filters is how the user reads back what they searched for
      const dropped = Object.keys(filterModel).some(columnId => !current[columnId])
      if (!dropped) return
      // The model the rows already answer to, so putting it back does not filter again
      this.setFilterModelQuietly(filterModel)
    },
    clearSelection () {
      if (this.emitSelect) {
        this.$emit('onSelect', [])
      } else {
        this.$store.dispatch('updateTransactionSelection', [])
      }
    },
    async applyFiltersonGrid (currentFilters, extendedFilters) {
      if (!this.gridApi) return
      this.extendedFilters = extendedFilters
      this.gridApi.setFilterModel(currentFilters)
    },
    onFilterChanged (params) {
      if (!params || !this.gridApi) return
      if (this.quietFilterWrites > 0) {
        this.quietFilterWrites -= 1
        return
      }
      if (!this.serverFilterConfig) return    // Views that still filter client side keep doing so
      const filterModel = this.activeFilterModel(this.gridApi.getFilterModel())
      if (this.filterModelKey(filterModel) === this.lastFilterModel) return
      this.gridApi.showLoadingOverlay()
      if (this.filterDebounceTimer) {
        clearTimeout(this.filterDebounceTimer)
      }
      this.filterDebounceTimer = setTimeout(() => {
        const model = filterModel
        this.fetchFilteredRows(this.buildFilterQuery(model, 1, this.extendedFilters), model)
      }, 250)
    },
    exportToExcel () {
      this.gridApi.exportDataAsExcel()
    },
    updateSelection (gridApi) {
      const api = gridApi || this.gridApi
      if (!api) return
      let selected = []
      api.forEachNode((row) => {
        if (row && row.selected && row.data) {
          selected.push(row.data)
        }
      })
      if (this.emitSelect) {
        this.$emit('onSelect', selected)
      } else {
        let notThisPage = this.selection.filter(v => v.page !== this.currentPage)
        this.$store.dispatch('updateTransactionSelection', [...notThisPage, ...selected])
      }
    },
    prepareColumns (columns) {
      this.actionsColumn.cellRendererParams = {
        clickhandleVoidDialogVisible: this.clickhandleVoidDialogVisible,
        postPayment: this.postPayment,
        loadingRelated: this.loadingRelated,
        postingPayment: this.postingPayment,
        voidingPayment: this.voidingPayment,
        postingCheck: this.postingCheck
      }
      if (columns && columns.length) {
        return this.aggridColumns.map((c) => this[c])
      } else {
        return [
          this.rowSelect,
          this.ledgerColumn,
          this.numberColumn,
          this.vendorColumn,
          this.descriptionColumn,
          this.amountColumn,
          this.batchColumn,
          this.actionsColumn
        ]
      }
    },
    deselectAll () {
      this.gridOptions.api.deselectAll()
    },
    clickhandleVoidDialogVisible (data) {
      this.$emit('handleVoidDialogVisible', data)
    },
    postPayment (data) {
      this.$emit('postPayment', data)
    },
    getLoading () {
      return this.loadingRelated
    },
    getVoiding () {
      return this.voidingPayment
    },
    getPosting () {
      return this.postingPayment
    },
    restoreSelection (data, key = 'id', page = this.currentPage) {
      if (!(data && data.length)) return
      const thisPageSelectedIds = data
        .filter(v => v.page === page)
        .map(v => v[key])
      this.gridOptions.api.forEachNode(node => node.setSelected(thisPageSelectedIds.includes(node.data[key])))
    },
    clearStore () {
      console.log('clearStore')
      if (this.gridApi) {
        try {
          this.gridApi.destroy()
        } catch (e) {
          console.log(e)
        }
      }
      this.selection = []
      this.gridApi = null
      this.columnApi = null
      this.extendedFilters = {}
      if (this.gridOptions) {
        this.gridOptions.api = null
        this.gridOptions.columnApi = null
      }
      this.rows = null
    }
  },
  beforeMount () {
    this.clearStore()
    this.defaultColDefs = this.prepareColumns(this.aggridColumns)
    this.defaultGridOptions = {
      singleClickEdit: true,
      undoRedoCellEditing: false,
      enterMovesDown: true,
      enterMovesDownAfterEdit: true,
      rowSelection: 'multiple',
      defaultColDef: {
        resizable: true,
        menuTabs: [],
        suppressSizeToFit: false
      },
      context: {
        tableName: this.name
      },
      popupParent: document.body,
      enableRangeSelection: true,
      animateRows: true,
      suppressRowClickSelection: true,
      suppressCellSelection: false,
      // Classes and events
      rowClassRules: {
        'ag-error': (params) => params.node.aggData && !this.hasCompleteAchData(params.node.aggData.vendorResource)
      },
      onGridReady: (params) => this.onGridReady(params),
      onFirstDataRendered: (params) => this.onFirstDataRendered(params),
      onSelectionChanged: (params) => {
        this.updateSelection(params.api)
      },
      onFilterChanged: (params) => {
        this.onFilterChanged(params)
      },
      getContextMenuItems: (params) => {
        const defaultItems = ['copy', 'copyWithHeaders', 'separator', 'export']
        if (params.column && (params.column.getColId() === 'number' || params.column.getColId() === 'ledger')) {
          defaultItems.unshift({
            name: 'Open in New Tab',
            action: () => {
              const index = this.$route.path.lastIndexOf('/')
              const path = `${this.$route.path}${index + 1 !== this.$route.path.length ? '/' : ''}${params.node.data.id}`
              const url = path
              if (url) {
                window.open(url, '_blank')
              }
            },
            icon: '<span class="ag-icon ag-icon-link"></span>'
          })
        }
        return defaultItems
      }
    }
    this.frameworkComponents = {
      agGridBtnRender: AgGridBtnRender,
      agGridSearchFilter: AgGridSearchFilter,
      agGridSearchFloatingFilter: AgGridSearchFloatingFilter,
      agGridTextFloatingFilter: AgGridTextFloatingFilter,
      agGridSingleSelectFilter: AgGridSingleSelectFilter,
      agGridDateRangeFilter: AgGridDateRangeFilter,
      agGridDateRangeFloatingFilter: AgGridDateRangeFloatingFilter
    }
  },
  beforeDestroy () {
    if (this.$el) {
      this.$el.removeEventListener('click', this.handleGridClick)
    }
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer)
      this.filterDebounceTimer = null
    }
    this.clearStore()
  },
  watch: {
    aggridColumns (val) {
      this.gridOptions.api.setColumnDefs([])
      this.gridOptions.api.setColumnDefs(this.prepareColumns(val))
    },
    selection (val) {
      if (this.gridApi) {
        const selectedData = this.gridApi.getSelectedRows()
        if (!(val && val.length) && selectedData && selectedData.length) {
          this.gridOptions.api.deselectAll()
        }
      }
    },
    isFilterLoading (value) {
      if (!this.gridApi) return
      this.$nextTick(() => {
        if (value) {
          this.gridApi.showLoadingOverlay()
        } else if (this.rows && this.rows.length) {
          this.gridApi.hideOverlay()
        } else {
          this.gridApi.showNoRowsOverlay()
        }
      })
    }
  }
})
</script>

<template>
  <ag-grid-vue
    :name="name"
    :ref="name"
    :class="classes"
    :rowHeight="rowHeight"
    :style="{width, margin: '17px'}"
    :column-defs="columnDefs"
    :row-data="rows"
    :grid-options="gridOptions"
    :dom-layout="domLayout"
    :frameworkComponents="frameworkComponents"
  />
</template>
<style scoped>
.ag-theme-balham .ag-row .ag-cell {
  display: flex !important;
  justify-content: center !important; /* align horizontal */
  align-items: center !important;
}
</style>
<style>
  .ag-filter-apply-panel .ag-standard-button {
    -webkit-appearance: none;
    appearance: none;
    outline: 0;
    display: inline-block;
    box-sizing: border-box;
    margin: 0;
    padding: 7px 9px;
    border: 1px solid #c4c4c4;
    border-radius: 4px;
    background: #fff;
    color: #1f2d3d;
    font-size: 12px;
    line-height: 1;
    text-align: center;
    white-space: nowrap;
    cursor: pointer;
  }
  .ag-filter-apply-panel .ag-standard-button + .ag-standard-button {
    margin-left: 8px;
  }
  .ag-filter-apply-panel .ag-standard-button:hover,
  .ag-filter-apply-panel .ag-standard-button:focus {
    color: #20a0ff;
    border-color: #20a0ff;
  }
  .ag-filter-apply-panel .ag-standard-button:active {
    color: #1d90e6;
    border-color: #1d90e6;
    outline: 0;
  }
  /* Applying is the primary action, the way it is in the dialogs */
  .ag-filter-apply-panel [ref="applyFilterButton"] {
    background-color: #20a0ff;
    border-color: #20a0ff;
    color: #fff;
  }
  .ag-filter-apply-panel [ref="applyFilterButton"]:hover,
  .ag-filter-apply-panel [ref="applyFilterButton"]:focus {
    background: #4db3ff;
    border-color: #4db3ff;
    color: #fff;
  }
  .ag-filter-apply-panel [ref="applyFilterButton"]:active {
    background: #1d90e6;
    border-color: #1d90e6;
    color: #fff;
  }
  .ag-filter-from.ag-filter-date-from, .ag-filter-to.ag-filter-date-to {
    margin: 10px 0px !important;
  }
</style>


// WEBPACK FOOTER //
// src/components/SigmaClientNew/TransactionsConfPaymentList.vue